import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowLeft, ArrowUpRight } from "lucide-react";
import { useLenis } from "@/hooks/useLenis";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { StickyCTA } from "@/components/landing/StickyCTA";
import { CtaButton } from "@/components/landing/CtaButton";
import { Reveal, MaskLines } from "@/components/landing/Reveal";
import { FOREX_PAGES, getForexPage } from "@/constants/forexPages";

const setMeta = (name, content, attr = "name") => {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export default function ForexPage() {
  useLenis();
  const { slug } = useParams();
  const page = getForexPage(slug);

  useEffect(() => {
    if (!page) return;
    document.title = page.title;
    setMeta("description", page.description);
    setMeta("keywords", page.tags.join(", "));
    setLink("canonical", `/forex/${page.slug}`);
    setMeta("og:title", page.title, "property");
    setMeta("og:description", page.description, "property");
    setMeta("og:image", page.img, "property");
    setMeta("og:url", `/forex/${page.slug}`, "property");
    window.scrollTo(0, 0);
  }, [page]);

  // No 404s — unknown slug redirects to the forex hub
  if (!page) return <Navigate to="/forex" replace />;

  const others = FOREX_PAGES.filter((p) => p.slug !== page.slug);

  return (
    <div className="App grain relative">
      <Nav />
      <main>
        {/* Hero / header — key term in eyebrow + H1 */}
        <section className="relative overflow-hidden pb-16 pt-32 sm:pt-36">
          <div
            className="pointer-events-none absolute left-1/2 top-24 h-[55vh] w-[55vh] -translate-x-1/2 rounded-full opacity-[0.15] blur-3xl"
            style={{ background: "radial-gradient(circle, #d4af37, transparent 65%)" }}
          />
          <div className="mx-auto max-w-5xl px-6 text-center sm:px-8">
            <Reveal>
              <Link to="/forex" data-testid="breadcrumb-forex" className="mb-6 inline-flex items-center gap-2 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#d4af37] hover:underline">
                <ArrowLeft className="h-3 w-3" /> Forex Coaching
              </Link>
            </Reveal>
            <h1 data-testid="forex-h1" className="font-['Playfair_Display'] text-4xl font-semibold capitalize leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
              <MaskLines className="block" lineClass="text-white-gold" lines={[page.key]} />
            </h1>
            <Reveal delay={0.15}>
              <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-[#a1a1aa] sm:text-lg">{page.intro}</p>
            </Reveal>
            <Reveal delay={0.3} className="mt-9 flex justify-center">
              <CtaButton testid="forex-hero-cta" />
            </Reveal>
          </div>
          <Reveal delay={0.2} className="mx-auto mt-14 max-w-5xl px-6 sm:px-8">
            <div className="corner-frame relative overflow-hidden">
              <div className="absolute -inset-2 border border-[#d4af37]/30" />
              <img src={page.img} alt={`${page.key} — Shabz Fazl`} data-testid="forex-hero-image" className="aspect-[16/9] w-full object-cover" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />
            </div>
          </Reveal>
        </section>

        {/* What you'll learn */}
        <section className="relative border-t border-white/10 py-16 sm:py-20">
          <div className="mx-auto max-w-4xl px-6 sm:px-8">
            <h2 className="text-center font-['Playfair_Display'] text-2xl font-medium tracking-tight sm:text-3xl">
              <span className="gold-metallic-text capitalize">What you&rsquo;ll master in {page.key}</span>
            </h2>
            <ul className="mx-auto mt-10 grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-2">
              {page.learn.map((it, i) => (
                <Reveal key={i} delay={(i % 2) * 0.06}>
                  <li data-testid={`forex-learn-${i}`} className="gold-toptrim flex items-start gap-3 border border-white/10 bg-[#0e0e10] p-5">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#d4af37]" strokeWidth={2.2} />
                    <span className="text-sm leading-relaxed text-[#c9c9c2]">{it}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>

        {/* Body sections — key term in each H2 + body */}
        <section className="relative border-t border-white/10 py-16 sm:py-24">
          <div className="mx-auto max-w-3xl space-y-14 px-6 sm:px-8">
            {page.sections.map((s, i) => (
              <Reveal key={i} delay={0.05}>
                <article data-testid={`forex-section-${i}`}>
                  <h2 className="font-['Playfair_Display'] text-2xl font-medium tracking-tight capitalize sm:text-3xl">
                    <span className="gold-metallic-text">{s.h2}</span>
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-[#a1a1aa] sm:text-lg">{s.body}</p>
                </article>
              </Reveal>
            ))}
            <Reveal>
              <div className="gold-trim flex flex-col items-start gap-5 p-8 shadow-[0_0_40px_rgba(212,175,55,0.08)] sm:flex-row sm:items-center sm:justify-between">
                <p className="max-w-md text-sm leading-relaxed text-[#a1a1aa]">
                  Coaching is <strong className="text-[#f5f5f0]">paid only</strong> and by application. If you&rsquo;re serious about {page.key}, message Shabz directly.
                </p>
                <CtaButton testid="forex-body-cta" className="shrink-0" />
              </div>
            </Reveal>
          </div>
        </section>

        {/* Tags (20) */}
        <section className="relative border-t border-white/10 py-14">
          <div className="mx-auto max-w-6xl px-6 sm:px-8">
            <h3 className="mb-5 text-center font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">Tags</h3>
            <div className="flex flex-wrap justify-center gap-2">
              {page.tags.map((t, i) => (
                <span key={i} data-testid={`forex-tag-${i}`} className="border border-white/10 px-3 py-1.5 text-xs text-[#8a8a92] transition-colors hover:border-[#d4af37]/40 hover:text-[#d4af37]">{t}</span>
              ))}
            </div>
          </div>
        </section>

        {/* Search terms (10) */}
        <section className="relative border-t border-white/10 py-14">
          <div className="mx-auto max-w-6xl px-6 sm:px-8">
            <h3 className="mb-5 text-center font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">Popular Searches</h3>
            <ul className="mx-auto grid max-w-4xl grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2">
              {page.searchTerms.map((t, i) => (
                <li key={i} data-testid={`forex-search-${i}`} className="text-xs leading-relaxed text-[#5f5f66]">{t}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Cross links — no orphan pages */}
        <section className="relative border-t border-white/10 py-16">
          <div className="mx-auto max-w-6xl px-6 sm:px-8">
            <h3 className="mb-8 text-center font-['Playfair_Display'] text-2xl text-[#f5f5f0]">Explore more forex coaching</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((p) => (
                <Link key={p.slug} to={`/forex/${p.slug}`} data-testid={`forex-xlink-${p.slug}`} className="group flex items-center justify-between border border-white/10 bg-[#0e0e10] p-5 capitalize transition-colors hover:border-[#d4af37]/40 hover:bg-[#141416]">
                  <span className="text-sm text-[#c9c9c2] group-hover:text-[#d4af37]">{p.key}</span>
                  <ArrowUpRight className="h-4 w-4 text-[#d4af37]" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCTA />
    </div>
  );
}
